import { gql, useMutation } from '@apollo/client';
import * as React from 'react';

import { CreateBlogDataInput_V2 } from '@/graphql-types/globalTypes';
import { logError } from '@/imports/logging/ClientLogger';
import { IAnalyticsLogger } from '@/imports/ui/amplitude/types';

import {
  useCreateBlog_createBlogV2,
  useCreateBlog_createBlogV2Variables,
} from './graphql-types/useCreateBlog_createBlogV2';

const createBlogWithPostAST = gql`
  mutation useCreateBlog_createBlogV2($input: CreateBlogInput_V2!) {
    createBlogV2(input: $input) {
      data {
        author {
          _id
          userOnboardingChecklistState {
            userHasPosted
          }
        }
        _id
        group {
          _id
          isGlobalSingleton
        }
      }
    }
  }
`;

type BlogMutationOptions = {
  analyticsClient: IAnalyticsLogger;
  publish: boolean;
  isAnnouncement: boolean;
  alreadyPublished: boolean;
  scheduledPublishDate: Date | null;
};

type CreateBlogOptions = {
  setPostId: React.Dispatch<React.SetStateAction<string | undefined>>;
  blog: Omit<CreateBlogDataInput_V2, 'published'>;
  options: BlogMutationOptions;
};

export const useCreateBlog = (): {
  createBlog: (options: CreateBlogOptions) => Promise<boolean>;
} => {
  const [createFunc] = useMutation<
    useCreateBlog_createBlogV2,
    useCreateBlog_createBlogV2Variables
  >(createBlogWithPostAST, {
    refetchQueries: ['DraftsPage_GetPosts'],
  });

  const createBlog = React.useCallback(
    async (options: CreateBlogOptions) => {
      const {
        analyticsClient,
        isAnnouncement,
        alreadyPublished,
        publish,
        scheduledPublishDate,
      } = options.options;
      const { blog } = options;

      try {
        const result = await createFunc({
          variables: {
            input: {
              data: {
                ...blog,
                published: publish,
                scheduledPublishDate:
                  scheduledPublishDate?.toISOString() ?? null,
              },
              isAnnouncement: isAnnouncement,
            },
          },
          update(cache: any) {
            cache.evict({
              id: 'ROOT_QUERY',
              fieldName: 'activityByAuthorId',
            });
            cache.gc();
          },
        });

        const resultBlogPost = result.data?.createBlogV2?.data;
        if (resultBlogPost?.__typename !== 'BlogV2') {
          throw new Error('no blogv2 returned from createBlogWithPostV2');
        }

        options.setPostId(resultBlogPost?._id);

        if (!alreadyPublished && publish) {
          const postedIn = !!resultBlogPost.group?.isGlobalSingleton
            ? 'general'
            : !!blog.organizationId
            ? 'organization'
            : 'group';
          analyticsClient.logEvent_comm_click_publish({
            'banner image': !!blog.blogBannerImageId,
            'added tags': !!blog.tagIds && blog.tagIds.length > 0,
            announcement: !!isAnnouncement,
            'posted in': postedIn,
            'is hosted by sponsor': false,
          });
        }
        return true;
      } catch (err) {
        logError(err);
        return false;
      }
    },
    [createFunc]
  );

  return {
    createBlog,
  };
};
